import React from 'react';
import { motion } from 'framer-motion';
import { ChefHat, Clock, Users, Star, Phone, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ServiceNavigation from '@/components/ServiceNavigation';

const CateringPage = () => {
  const packages = [
    {
      name: "Ejecutivo",
      price: "Desde $25,000",
      people: "10-20 personas",
      description: "Perfecto para reuniones corporativas y eventos de negocio",
      features: [
        "Selección de ostras frescas",
        "Mariscos premium", 
        "Acompañamientos gourmet",
        "Servicio básico",
        "2 horas de evento"
      ],
      color: "ocean"
    },
    {
      name: "Premium",
      price: "Desde $45,000",
      people: "20-40 personas", 
      description: "Experiencia completa para celebraciones especiales",
      features: [
        "Ostras premium variadas",
        "Mariscos frescos y cocidos",
        "Bar de mariscos completo",
        "Chef especializado",
        "Servicio personalizado",
        "4 horas de evento",
        "Maridajes incluidos"
      ],
      color: "jade",
      popular: true
    },
    {
      name: "Luxury",
      price: "Desde $80,000",
      people: "40+ personas",
      description: "La experiencia gastronómica más exclusiva",
      features: [
        "Selección premium de ostras",
        "Mariscos de lujo",
        "Oyster Bar móvil completo",
        "Chef ejecutivo",
        "Sommelier especializado", 
        "Servicio VIP",
        "Evento completo",
        "Decoración temática"
      ],
      color: "seagreen"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pearl-50/50 via-white to-ocean-25/30">
      {/* Service Navigation */}
      <div className="pt-20">
        <ServiceNavigation currentService="catering" />
      </div>

      {/* Hero Section */}
      <motion.div
        className="relative py-16 px-4 sm:px-6 lg:px-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            className="inline-flex items-center gap-3 mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="w-16 h-16 bg-gradient-to-r from-ocean-500 to-ocean-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
              <ChefHat className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-transparent bg-gradient-to-r from-ocean-600 to-jade-600 bg-clip-text font-heading">
                Catering Marino
              </h1>
            </div>
          </motion.div>
          
          <motion.p 
            className="text-xl text-deep-600 max-w-2xl mx-auto leading-relaxed mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Llevamos la excelencia gastronómica del mar a tu evento. 
            Nuestro servicio de catering especializado transforma cualquier celebración 
            en una experiencia culinaria memorable.
          </motion.p>

          <motion.div
            className="flex flex-wrap justify-center gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Badge className="bg-ocean-100 text-ocean-700 px-3 py-1">
              <Clock className="w-4 h-4 mr-1" />
              Servicio 24/7
            </Badge>
            <Badge className="bg-jade-100 text-jade-700 px-3 py-1">
              <Users className="w-4 h-4 mr-1" />
              Desde 10 personas
            </Badge>
            <Badge className="bg-seagreen-100 text-seagreen-700 px-3 py-1">
              <Star className="w-4 h-4 mr-1" />
              Chef especializado
            </Badge>
          </motion.div>
        </div>
      </motion.div>

      {/* Packages Section */}
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-deep-700 mb-4 font-heading">
            Nuestros Paquetes
          </h2>
          <p className="text-deep-600 max-w-2xl mx-auto">
            Elegí el paquete perfecto para tu evento. Todos incluyen productos frescos 
            de la Patagonia y servicio profesional.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.name}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              <Card className={`h-full bg-white/90 backdrop-blur-sm border shadow-lg hover:shadow-2xl transition-all duration-500 rounded-2xl overflow-hidden relative ${pkg.popular ? 'border-jade-300 ring-2 ring-jade-200' : 'border-white/60'}`}>
                {pkg.popular && (
                  <div className="absolute top-4 right-4 z-10">
                    <Badge className="bg-jade-500 text-white px-3 py-1 font-semibold">
                      Popular
                    </Badge>
                  </div>
                )}
                
                <CardHeader className={`bg-gradient-to-r ${
                  pkg.color === 'ocean' ? 'from-ocean-50 to-ocean-100' :
                  pkg.color === 'jade' ? 'from-jade-50 to-jade-100' :
                  'from-seagreen-50 to-seagreen-100'
                } p-6`}>
                  <CardTitle className="text-2xl font-bold text-deep-700 mb-2">
                    {pkg.name}
                  </CardTitle>
                  <div className="space-y-2">
                    <p className={`text-3xl font-bold ${
                      pkg.color === 'ocean' ? 'text-ocean-600' :
                      pkg.color === 'jade' ? 'text-jade-600' :
                      'text-seagreen-600'
                    }`}>
                      {pkg.price}
                    </p>
                    <p className="text-deep-600 font-medium">{pkg.people}</p>
                    <p className="text-sm text-deep-600">{pkg.description}</p>
                  </div>
                </CardHeader>
                
                <CardContent className="p-6">
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-deep-600">
                        <CheckCircle className="w-4 h-4 text-jade-500 mr-3 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button className={`w-full bg-gradient-to-r ${
                    pkg.color === 'ocean' ? 'from-ocean-500 to-ocean-600' :
                    pkg.color === 'jade' ? 'from-jade-500 to-jade-600' :
                    'from-seagreen-500 to-seagreen-600'
                  } hover:opacity-90 text-white rounded-xl py-3 font-semibold transition-all duration-300`}>
                    Solicitar Cotización
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div
        className="bg-gradient-to-r from-ocean-500/10 to-jade-500/10 py-16"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
      >
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-deep-700 mb-4">
            ¿Listo para crear tu evento perfecto?
          </h3>
          <p className="text-deep-600 mb-8 max-w-2xl mx-auto">
            Contactanos para una consulta personalizada y cotización sin compromiso. 
            Nuestro equipo te ayudará a planificar el evento marino perfecto.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-ocean-500 to-jade-500 hover:from-ocean-600 hover:to-jade-600 text-white rounded-xl px-8 py-3 font-semibold"
            >
              <Phone className="w-4 h-4 mr-2" />
              Llamar Ahora
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-ocean-300 text-ocean-600 hover:bg-ocean-50 rounded-xl px-8 py-3 font-semibold"
            >
              Consulta Online
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CateringPage;
